import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { InformationSource } from "@/types/information-source/information-source.types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table/index";
import { TableHeader } from "@/components/ui/table-header/index";
import { Plus } from "lucide-react";
import { InformationSourceForm } from "./information-sources/InfoSourceForm";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog/index";
import { useInfoSourceTable } from "@/hooks/information-sources/useInfoSourceTable";
import { useInfoSourceColumns } from "./information-sources/columns";
import { useConfirmationDialog } from "@/hooks/information-sources/useConfirmationDialog";

export const InfoSources = () => {
  const { t } = useLanguage();
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  const [formOpen, setFormOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState<InformationSource | null>(null);

  const {
    filteredSources,
    loading,
    refetch,
    searchTerm,
    setSearchTerm,
    showArchived,
    setShowArchived,
    nameFilters,
    setNameFilters,
    yearFilters,
    setYearFilters,
    clearFilters,
    nameOptions,
    yearOptions,
  } = useInfoSourceTable();

  const {
    isOpen: confirmDialogOpen,
    confirmAction,
    isLoading: isConfirmActionLoading,
    openDialog,
    closeDialog,
    handleConfirm,
  } = useConfirmationDialog(refetch);

  const handleAddSource = () => {
    setSelectedSource(null);
    setFormOpen(true);
  };

  const handleEditSource = (source: InformationSource) => {
    setSelectedSource(source);
    setFormOpen(true);
  };

  const handleArchiveAction = (source: InformationSource) => {
    openDialog({
      type: source.isArchived ? "restore" : "archive",
      sourceId: source.id,
      sourceName: source.name,
    });
  };

  const columns = useInfoSourceColumns(t, handleEditSource, handleArchiveAction);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filters = [
    {
      label: t("infoSources.filters.name"),
      options: nameOptions,
      selectedValues: nameFilters,
      onChange: setNameFilters,
      showSearch: true,
    },
    {
      label: t("infoSources.filters.year"),
      options: yearOptions,
      selectedValues: yearFilters,
      onChange: setYearFilters,
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-lg font-semibold">{t("infoSources.title")}</h2>
        {!isAssimagraUser && (
          <Button
            onClick={handleAddSource}
            className="bg-teal-600 text-white hover:bg-teal-700 self-start sm:self-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("infoSources.addButton")}
          </Button>
        )}
      </div>

      <TableHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t("infoSources.searchPlaceholder")}
        filters={filters}
        toggle={[
          {
            label: t("infoSources.showArchived"),
            checked: showArchived,
            onCheckedChange: setShowArchived,
          },
        ]}
        onClearAll={clearFilters}
      />

      <div className="overflow-hidden">
        <DataTable columns={columns} data={filteredSources} pageSize={10} className="w-full" minWidth="auto" />
      </div>

      <InformationSourceForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        sourceToEdit={selectedSource}
        onSuccess={refetch}
      />

      {confirmAction && (
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={closeDialog}
          onConfirm={handleConfirm}
          title={
            confirmAction.type === "archive"
              ? t("infoSources.confirmArchive.title")
              : t("infoSources.confirmRestore.title")
          }
          description={t(
            confirmAction.type === "archive"
              ? "infoSources.confirmArchive.description"
              : "infoSources.confirmRestore.description"
          )}
          confirmText={
            isConfirmActionLoading
              ? t(
                  confirmAction.type === "archive"
                    ? "infoSources.confirmArchive.actionLoading"
                    : "infoSources.confirmRestore.actionLoading"
                )
              : t(
                  confirmAction.type === "archive"
                    ? "infoSources.confirmArchive.action"
                    : "infoSources.confirmRestore.action"
                )
          }
          cancelText={t("infoSources.cancel")}
          variant={confirmAction.type === "archive" ? "destructive" : "default"}
          isLoading={isConfirmActionLoading}
        />
      )}
    </div>
  );
};
